import React, { useState, useRef } from "react";
import axios from "axios";
import * as cheerio from "cheerio";
import JSEncrypt from "jsencrypt";

const PSALoginBot = () => {
  const [loginData, setLoginData] = useState({
    userId: "",
    userPwd: "",
    captcha: "",
  });
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [csrfToken, setCsrfToken] = useState("");
  const [captchaImage, setCaptchaImage] = useState("");
  const iframeRef = useRef(null);

  const baseURL = "https://www.psaonline.utiitsl.com/psapanservices"; // Replace with actual URL

  // Step 1: Fetch the login page and extract CSRF token & captcha
  const initializeLogin = async () => {
    try {
      setStatus("Initializing login session...");
      setLoading(true);

      const response = await axios.get(
        `${baseURL}/forms/login.html/loginHome`,
        {
          headers: {
            "User-Agent":
              "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36",
          },
        },
      );

      const $ = cheerio.load(response.data);

      // Extract CSRF token
      const csrfMeta = $('meta[name="_csrf"]');
      const token = csrfMeta.attr("content") || "";
      setCsrfToken(token);

      // Extract captcha image
      const captchaUrl = `${baseURL}/captcha.jpg?${Date.now()}`;
      setCaptchaImage(captchaUrl);

      setStatus("Login page loaded successfully");
      return true;
    } catch (error) {
      setStatus(`Error initializing: ${error.message}`);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Encrypt password using RSA public key
  const encryptPassword = (password) => {
    const publicKey =
      "MIGfMA0GCSqGSIb3DQEBAQUAA4GNADCBiQKBgQDwTGjphXZn/aiTEQous1/yUXBPhcgXSTNXXyMCfGa2ldFAtvAJpb1F7Fzc4bhkhZhILzxPydSrvDNFzwFSg07U/kkK1g9NKUgvXqm/A3PKjaxp1UI4WxLFMkM48MQR/pAExismK+1WnMxBALId3m/vUT0iQo5PdGkzxVNFThewGwIDAQAB";

    const encrypt = new JSEncrypt();
    encrypt.setPublicKey(publicKey);
    return encrypt.encrypt(password);
  };

  // Step 3: Handle CAPTCHA (Note: This is a simple approach, real CAPTCHAs need advanced solutions)
  const handleCaptcha = async () => {
    // For actual CAPTCHA solving, you'd need:
    // 1. OCR service (like Tesseract.js)
    // 2. CAPTCHA solving service (like 2captcha)
    // 3. Manual entry (recommended for testing)

    // Example with manual input (you can replace with automated solution)
    return loginData.captcha;
  };

  // Step 4: Perform login
  const performLogin = async () => {
    if (!loginData.userId || !loginData.userPwd || !loginData.captcha) {
      setStatus("Please fill all fields");
      return;
    }

    try {
      setLoading(true);
      setStatus("Attempting login...");

      // Encrypt the password
      const encryptedPassword = encryptPassword(loginData.userPwd);

      // Prepare form data
      const formData = new FormData();
      formData.append("userId", loginData.userId.toUpperCase());
      formData.append("userPwd", encryptedPassword);
      formData.append("captcha", loginData.captcha);
      formData.append("_csrf", csrfToken);

      // Send login request
      const response = await axios.post(
        `${baseURL}/forms/login.html/loginHome`,
        formData,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            "X-CSRF-TOKEN": csrfToken,
            "User-Agent":
              "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36",
          },
        },
      );

      // Check login success
      if (response.status === 200) {
        // Check for redirect or successful login indicators
        const $ = cheerio.load(response.data);

        if (
          response.request.path.includes("home") ||
          $('a[href*="logout"]').length > 0
        ) {
          setStatus("Login successful!");
          // You can now navigate to dashboard or perform next actions
        } else {
          setStatus("Login failed. Check credentials or CAPTCHA.");
        }
      }
    } catch (error) {
      setStatus(`Login error: ${error.message}`);

      // If CAPTCHA is wrong, refresh it
      if (error.response && error.response.status === 400) {
        await refreshCaptcha();
      }
    } finally {
      setLoading(false);
    }
  };

  // Step 5: Refresh CAPTCHA
  const refreshCaptcha = async () => {
    try {
      const captchaUrl = `${baseURL}/captcha.jpg?${Date.now()}`;
      setCaptchaImage(captchaUrl);
      setLoginData((prev) => ({ ...prev, captcha: "" }));
      setStatus("CAPTCHA refreshed. Please try again.");
    } catch (error) {
      setStatus(`Failed to refresh CAPTCHA: ${error.message}`);
    }
  };

  // Step 6: Auto-fill with test credentials (for testing only)
  const autoFillTestCredentials = () => {
    setLoginData({
      userId: "TEST_USER",
      userPwd: "Test@123",
      captcha: "", // Still need to fill CAPTCHA
    });
    setStatus("Test credentials filled. Enter CAPTCHA to proceed.");
  };

  // Step 7: Check session timeout (simulating the timeout logic from the original site)
  const checkSessionStatus = () => {
    const STUPM = 30; // Session timeout in minutes (adjust as needed)
    const warningTime = (STUPM - 5) * 60 * 1000; // Warn 5 minutes before timeout

    setStatus(`Session will timeout after ${STUPM} minutes of inactivity`);

    // You can implement session monitoring logic here
    return {
      timeoutMinutes: STUPM,
      warningMinutes: 5,
    };
  };

  return (
    <div className="psa-login-bot">
      <h2>PSA Online Login Automation</h2>

      <div className="controls">
        <button onClick={initializeLogin} disabled={loading}>
          Initialize Session
        </button>

        <button onClick={checkSessionStatus}>Check Session Status</button>

        <button onClick={autoFillTestCredentials}>
          Auto-fill Test Credentials
        </button>
      </div>

      <div className="login-form">
        <div className="form-group">
          <label>User ID:</label>
          <input
            type="text"
            value={loginData.userId}
            onChange={(e) =>
              setLoginData({ ...loginData, userId: e.target.value })
            }
            placeholder="Enter User ID"
            onKeyUp={(e) => {
              // Auto-uppercase like the original site
              e.target.value = e.target.value.toUpperCase();
            }}
          />
        </div>

        <div className="form-group">
          <label>Password:</label>
          <input
            type="password"
            value={loginData.userPwd}
            onChange={(e) =>
              setLoginData({ ...loginData, userPwd: e.target.value })
            }
            placeholder="Enter Password"
          />
        </div>

        <div className="form-group">
          <label>CAPTCHA:</label>
          <div className="captcha-section">
            {captchaImage && (
              <img
                src={captchaImage}
                alt="CAPTCHA"
                className="captcha-image"
                onClick={refreshCaptcha}
                title="Click to refresh"
              />
            )}
            <button onClick={refreshCaptcha} className="refresh-btn">
              ↻ Refresh
            </button>
          </div>
          <input
            type="text"
            value={loginData.captcha}
            onChange={(e) =>
              setLoginData({ ...loginData, captcha: e.target.value })
            }
            placeholder="Enter CAPTCHA"
            maxLength="10"
          />
        </div>

        <button
          onClick={performLogin}
          disabled={loading || !csrfToken}
          className="login-btn"
        >
          {loading ? "Logging in..." : "Login"}
        </button>
      </div>

      <div className="status">
        <h3>Status:</h3>
        <p>{status}</p>
        {csrfToken && (
          <div className="session-info">
            <p>
              <strong>CSRF Token:</strong> {csrfToken.substring(0, 20)}...
            </p>
          </div>
        )}
      </div>

      <div className="features">
        <h3>Additional Features:</h3>
        <ul>
          <li>Automated password encryption (RSA)</li>
          <li>CSRF token handling</li>
          <li>CAPTCHA refresh mechanism</li>
          <li>Session timeout monitoring</li>
          <li>Form validation</li>
        </ul>
      </div>

      <style jsx>{`
        .psa-login-bot {
          max-width: 600px;
          margin: 20px auto;
          padding: 20px;
          border: 1px solid #ccc;
          border-radius: 8px;
          background: #f9f9f9;
        }
        .controls {
          margin-bottom: 20px;
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }
        button {
          padding: 8px 16px;
          background: #007bff;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
        }
        button:hover {
          background: #0056b3;
        }
        button:disabled {
          background: #ccc;
          cursor: not-allowed;
        }
        .login-form {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }
        .form-group {
          display: flex;
          flex-direction: column;
        }
        .form-group label {
          margin-bottom: 5px;
          font-weight: bold;
        }
        .form-group input {
          padding: 8px;
          border: 1px solid #ddd;
          border-radius: 4px;
        }
        .captcha-section {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 10px;
        }
        .captcha-image {
          height: 50px;
          border: 1px solid #ddd;
          cursor: pointer;
        }
        .refresh-btn {
          height: fit-content;
          background: #6c757d;
        }
        .login-btn {
          padding: 10px;
          background: #28a745;
          font-size: 16px;
        }
        .status {
          margin-top: 20px;
          padding: 15px;
          background: #fff;
          border: 1px solid #ddd;
          border-radius: 4px;
        }
        .features {
          margin-top: 20px;
          padding: 15px;
          background: #e9ecef;
          border-radius: 4px;
        }
        .features ul {
          margin: 10px 0 0 0;
          padding-left: 20px;
        }
      `}</style>
    </div>
  );
};

export default PSALoginBot;
